import { z } from 'zod'
import { ADMIN_ROLES, PERMISSIONS, can, type AdminRole, type Permission } from './permissions'

/* Spec COG-009 — shared part of the Admin API contract (/api/v1/admin/*). Each area keeps its own schemas in
   src/features/admin/<area>/contract.ts; the server imports them from there.

   GET  /api/v1/admin/me                    -> AdminMe
   GET  /api/v1/admin/<area>                -> the area's screen data (dataMode: 'example' | 'live')
   POST /api/v1/admin/<area>/<action>       -> AdminActionResult (same-origin, audited, permission checked on the server)
   POST /api/v1/admin/<area>/export         body ExportInput -> ExportResult (reason required; personal data needs
                                                                              'export.dados_pessoais'; addresses never leave) */

export const ADMIN_AREAS = [
    { slug: 'fichas', path: '/staff/fichas', label: 'Fichas', title: 'Fichas digitais', group: 'Live Portal', permission: 'fichas.ver' },
    { slug: 'pre-ativos', path: '/staff/pre-ativos', label: 'Pré-ativos', title: 'Pré-ativos e boas-vindas', group: 'Live Portal', permission: 'retencao' },
    { slug: 'recorrencias', path: '/staff/recorrencias', label: 'Recorrências', title: 'Recorrências e pagamentos', group: 'Receita', permission: 'recorrencias.ver' },
    { slug: 'retencao', path: '/staff/retencao', label: 'Retenção', title: 'Cancelamento e retenção', group: 'Receita', permission: 'retencao' },
    // Gerência only reviews (conciliacao.revisar) but must be able to open the screen to do it
    { slug: 'conciliacao', path: '/staff/conciliacao', label: 'Conciliação', title: 'Conciliação Braspag × CRM', group: 'Receita', permission: 'conciliacao', alsoOpenWith: ['conciliacao.revisar'] },
    { slug: 'moderacao', path: '/staff/moderacao', label: 'Moderação', title: 'Moderação de mensagens', group: 'Padrinho', permission: 'moderacao' },
    { slug: 'cartas', path: '/staff/cartas', label: 'Cartas', title: 'Prazos das cartas', group: 'Padrinho', permission: 'cartas' },
    { slug: 'ver-como', path: '/staff/ver-como', label: 'Ver como', title: 'Ver como padrinho', group: 'Padrinho', permission: 'ver_como' },
    { slug: 'parceiros', path: '/staff/parceiros', label: 'Campo e eventos', title: 'Equipe de campo, OSPs e Lives', group: 'Rede', permission: 'parceiros' },
    { slug: 'embaixadores', path: '/staff/embaixadores', label: 'Embaixadores', title: 'Embaixadores', group: 'Rede', permission: 'embaixadores' },
    { slug: 'inscricoes', path: '/staff/inscricoes', label: 'Inscrições', title: 'Inscrição de crianças', group: 'Rede', permission: 'inscricoes' },
    { slug: 'marketing', path: '/staff/marketing', label: 'Marketing', title: 'Marketing digital', group: 'Gestão', permission: 'marketing' },
    { slug: 'indicadores', path: '/staff/indicadores', label: 'Indicadores', title: 'Indicadores da base', group: 'Gestão', permission: 'indicadores' },
    { slug: 'equipe', path: '/staff/equipe', label: 'Equipe', title: 'Equipe e permissões', group: 'Gestão', permission: 'equipe.ver' },
] as const satisfies readonly { slug: string; path: string; label: string; title: string; group: string; permission: Permission; alsoOpenWith?: readonly Permission[] }[]

export type AdminArea = (typeof ADMIN_AREAS)[number]

/** Whether a role can open an area: its own permission or one of `alsoOpenWith`. */
export function canOpenArea(role: AdminRole, area: AdminArea): boolean {
    if (can(role, area.permission)) return true
    return 'alsoOpenWith' in area && area.alsoOpenWith.some(p => can(role, p))
}
export type AdminAreaSlug = AdminArea['slug']
export type AdminPath = AdminArea['path'] | '/staff/denuncia' | '/staff/hoje'

export const adminMeSchema = z.object({
    email: z.string(),
    name: z.string(),
    role: z.enum(ADMIN_ROLES),
    roleLabel: z.string(),
    permissions: z.array(z.enum(PERMISSIONS)),
})
export type AdminMe = z.infer<typeof adminMeSchema>

export const EXPORT_REASONS = ['conferencia', 'auditoria', 'pedido_coordenacao', 'outro'] as const
export const EXPORT_REASON_LABELS: Record<(typeof EXPORT_REASONS)[number], string> = {
    conferencia: 'Conferência',
    auditoria: 'Auditoria',
    pedido_coordenacao: 'Pedido da coordenação',
    outro: 'Outro',
}

export const exportInputSchema = z.object({
    format: z.enum(['xlsx', 'csv']),
    reason: z.enum(EXPORT_REASONS),
    /** Required (3+ chars) when reason is 'outro'. */
    note: z.string().trim().max(300).optional(),
    includePersonal: z.boolean().default(false),
    /** Free filter description from the screen ("Para revisar · Culto de domingo"), only for the audit line. */
    scope: z.string().trim().max(120).optional(),
}).refine(v => v.reason !== 'outro' || (v.note?.length ?? 0) >= 3, { message: 'note_required', path: ['note'] })
export type ExportInput = z.infer<typeof exportInputSchema>

export const exportResultSchema = z.object({
    fileName: z.string(),
    rows: z.number().int(),
    includePersonal: z.boolean(),
    /** "Registrado no histórico de Equipe" line for the screen. */
    message: z.string(),
})
export type ExportResult = z.infer<typeof exportResultSchema>

export const adminActionResultSchema = z.object({ ok: z.literal(true), message: z.string() })
export type AdminActionResult = z.infer<typeof adminActionResultSchema>

/* ── Hoje (GET /api/v1/admin/hoje) and search (GET /api/v1/admin/search?q=) ──────────────────────────────────
   Hoje: what needs the person today, across the areas their role can open (most urgent first).
   Search: padrinhos, fichas, protocolos, igrejas… across those same areas (2+ chars; max 20 results). */

export const TODAY_URGENCY = ['atrasado', 'hoje', 'em_breve', 'info'] as const

export const todayItemSchema = z.object({
    id: z.string(),
    area: z.string(),
    /** "3 estornos esperando a sua aprovação" */
    title: z.string(),
    /** Short context: "o mais antigo há 2 dias · R$ 1.240" */
    detail: z.string().optional(),
    count: z.number().int().nonnegative(),
    urgency: z.enum(TODAY_URGENCY),
    /** Where to act, e.g. "/staff/recorrencias" (optionally with ?search). */
    path: z.string(),
    /** Button label: "Aprovar", "Revisar", "Relatar"… */
    actionLabel: z.string(),
})
export type TodayItem = z.infer<typeof todayItemSchema>

export const todaySchema = z.object({
    dataMode: z.enum(['example', 'live']),
    /** "segunda, 20 de outubro" (Brasília) */
    dateLabel: z.string(),
    items: z.array(todayItemSchema),
    /** Areas with nothing pending (shown as "em dia"). */
    clear: z.array(z.object({ area: z.string(), label: z.string() })),
})
export type Today = z.infer<typeof todaySchema>

export const searchResultSchema = z.object({
    id: z.string(),
    kind: z.enum(['padrinho', 'ficha', 'protocolo', 'igreja', 'crianca', 'pessoa', 'campanha', 'pedido', 'outro']),
    /** Masked like the screens do ("Mariana C. R."). */
    label: z.string(),
    sub: z.string().optional(),
    area: z.string(),
    path: z.string(),
})
export type SearchResult = z.infer<typeof searchResultSchema>
export const searchSchema = z.object({ q: z.string(), results: z.array(searchResultSchema) })
export type SearchResponse = z.infer<typeof searchSchema>
